/*
==============================================================================
Código hecho por Rcrea
Archivo: assets/section-rcrea-guten-community.js
Sección: Güten Brew | Comunidad
Función:
- Play y pausa dentro de la card
- Pantalla completa nativa del navegador, sin modal intermedio
- Controles nativos visibles en pantalla completa
- Video completo con object-fit contain
- Compatibilidad con Shopify Video, MP4, YouTube y Cloudflare
- Carrusel con loop, dots, flechas, arrastre y autoplay
- Microanimaciones y contadores
==============================================================================
*/
(() => {
  const ROOT_SELECTOR = "[data-gc-community]";
  const CARD_SELECTOR = "[data-gc-media-card]";
  const VIDEO_SELECTOR = "[data-gc-video]";
  const IFRAME_SELECTOR = "[data-gc-iframe]";
  const PLAY_SELECTOR = "[data-gc-play]";
  const EXPAND_SELECTOR = "[data-gc-expand]";
  const COUNTER_SELECTOR = "[data-gc-counter]";
  const REVEAL_SELECTOR = "[data-gc-reveal]";
  const INIT_ATTR = "data-gc-initialized";
  const mobileQuery = window.matchMedia("(max-width: 749px)");
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const getRoots = (root = document) => {
    const roots = [];
    if (root.matches?.(ROOT_SELECTOR)) roots.push(root);
    root.querySelectorAll?.(ROOT_SELECTOR).forEach((item) => roots.push(item));
    return roots;
  };
  const addCleanup = (root, callback) => {
    if (!Array.isArray(root._gcCleanups)) root._gcCleanups = [];
    root._gcCleanups.push(callback);
  };
  const getTrack = (root) => root.querySelector("[data-gc-media-track]");
  const getCards = (track) => track ? Array.from(track.querySelectorAll(CARD_SELECTOR)) : [];
  const getGap = (track) => {
    if (!track) return 0;
    const style = window.getComputedStyle(track);
    return Number.parseFloat(style.columnGap || style.gap || "0") || 0;
  };
  const getPerPage = (track) => {
    const cards = getCards(track);
    if (!track || !cards.length) return 1;
    const cardWidth = cards[0].getBoundingClientRect().width;
    const gap = getGap(track);
    if (!cardWidth) return 1;
    return Math.max(1, Math.floor((track.clientWidth + gap) / (cardWidth + gap)));
  };
  const getPageStarts = (track) => {
    const cards = getCards(track);
    if (!track || !cards.length) return [0];
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (maxScroll <= 2) return [0];
    const cardWidth = cards[0].getBoundingClientRect().width;
    const gap = getGap(track);
    const perPage = getPerPage(track);
    const step = Math.max(1, perPage * (cardWidth + gap));
    const starts = [0];
    let position = step;
    while (position < maxScroll - 2) {
      starts.push(position);
      position += step;
    }
    if (Math.abs(starts[starts.length - 1] - maxScroll) > 2) {
      starts.push(maxScroll);
    }
    return starts;
  };
  const getActivePage = (track, starts = getPageStarts(track)) => {
    if (!track || !starts.length) return 0;
    const current = Math.max(0, track.scrollLeft);
    let activeIndex = 0;
    let activeDistance = Infinity;
    starts.forEach((start, index) => {
      const distance = Math.abs(start - current);
      if (distance < activeDistance) {
        activeDistance = distance;
        activeIndex = index;
      }
    });
    return activeIndex;
  };
  const cancelScrollAnimation = (track) => {
    if (!track?._gcScrollFrame) return;
    window.cancelAnimationFrame(track._gcScrollFrame);
    track._gcScrollFrame = null;
  };
  const animateScroll = (track, target, duration, onComplete) => {
    if (!track) return;
    cancelScrollAnimation(track);
    const start = track.scrollLeft;
    const distance = target - start;
    if (Math.abs(distance) < 1 || duration <= 0) {
      track.scrollLeft = target;
      onComplete?.();
      return;
    }
    const startTime = performance.now();
    const ease = (progress) => {
      return progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
    };
    const frame = (time) => {
      const progress = Math.min((time - startTime) / duration, 1);
      track.scrollLeft = start + distance * ease(progress);
      if (progress < 1) {
        track._gcScrollFrame = window.requestAnimationFrame(frame);
      } else {
        track._gcScrollFrame = null;
        track.scrollLeft = target;
        onComplete?.();
      }
    };
    track._gcScrollFrame = window.requestAnimationFrame(frame);
  };
  const getActiveWrapper = (card) => {
    const hasMobile = card.classList.contains("gc-media-card--has-mobile");
    if (mobileQuery.matches && hasMobile) {
      return card.querySelector('[data-gc-media-wrapper="mobile"]');
    }
    return card.querySelector('[data-gc-media-wrapper="desktop"]');
  };
  const getActiveMedia = (card) => {
    const wrapper = getActiveWrapper(card);
    return wrapper ? wrapper.querySelector(`${VIDEO_SELECTOR},${IFRAME_SELECTOR}`) : null;
  };
  const isYouTubeIframe = (iframe) => {
    const source = String(iframe?.dataset.src || iframe?.src || "");
    return source.includes("youtube.com") ||
      source.includes("youtube-nocookie.com") ||
      source.includes("youtu.be");
  };
  const isIframeAutoplay = (iframe) => {
    const source = String(iframe?.dataset.src || "");
    return source.includes("autoplay=1") || source.includes("autoplay=true");
  };
  const appendUrlParameter = (url, name, value) => {
    if (!url) return url;
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set(name, value);
      return parsed.toString();
    } catch (error) {
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    }
  };
  const createPlayButton = (card) => {
    if (!card || card.querySelector(PLAY_SELECTOR)) return;
    if (!card.querySelector(`${VIDEO_SELECTOR},${IFRAME_SELECTOR}`)) return;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "gc-media-card__play";
    button.setAttribute("data-gc-play", "");
    button.setAttribute("aria-label", "Reproducir video");
    button.innerHTML = `
      <svg class="gc-media-card__play-icon gc-media-card__play-icon--play" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8.2 5.8 18.2 12 8.2 18.2z"></path>
      </svg>
      <svg class="gc-media-card__play-icon gc-media-card__play-icon--pause" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7.5 5.5h3.5v13H7.5zM13 5.5h3.5v13H13z"></path>
      </svg>
      <span class="gc-media-card__play-loader" aria-hidden="true"></span>
    `;
    card.appendChild(button);
  };
  const createExpandButton = (card) => {
    if (!card || card.querySelector(EXPAND_SELECTOR)) return;
    if (!card.querySelector(`${VIDEO_SELECTOR},${IFRAME_SELECTOR}`)) return;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "gc-media-card__expand";
    button.setAttribute("data-gc-expand", "");
    button.setAttribute("aria-label", "Abrir video en pantalla completa");
    button.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8.5 4.5h-4v4"></path>
        <path d="M15.5 4.5h4v4"></path>
        <path d="M19.5 15.5v4h-4"></path>
        <path d="M8.5 19.5h-4v-4"></path>
      </svg>
    `;
    card.appendChild(button);
  };
  const ensureVideoControls = (root) => {
    root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      createPlayButton(card);
      createExpandButton(card);
    });
  };
  const prepareVideo = (video) => {
    if (!video) return;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    if (video.dataset.autoplay === "true") {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute("muted", "");
    }
  };
  const playVideo = (video, unmute = false) => {
    if (!video) return Promise.resolve(false);
    prepareVideo(video);
    if (unmute) {
      video.muted = false;
      video.defaultMuted = false;
      video.removeAttribute("muted");
    } else if (video.dataset.autoplay === "true") {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute("muted", "");
    }
    const promise = video.play();
    if (promise && typeof promise.then === "function") {
      return promise.then(() => true).catch(() => false);
    }
    return Promise.resolve(true);
  };
  const pauseVideo = (video) => {
    if (!video) return;
    try {
      video.pause();
    } catch (error) {}
  };
  const loadIframe = (iframe, forceAutoplay = false) => {
    if (!iframe) return;
    let source = String(iframe.dataset.src || iframe.src || "");
    if (!source || source === "about:blank") return;
    if (forceAutoplay) {
      if (isYouTubeIframe(iframe)) {
        source = appendUrlParameter(source, "autoplay", "1");
        source = appendUrlParameter(source, "mute", "0");
        source = appendUrlParameter(source, "controls", "1");
        source = appendUrlParameter(source, "playsinline", "1");
        source = appendUrlParameter(source, "enablejsapi", "1");
      } else {
        source = appendUrlParameter(source, "autoplay", "true");
        source = appendUrlParameter(source, "muted", "false");
        source = appendUrlParameter(source, "controls", "true");
      }
      iframe.dataset.gcStarted = "true";
    }
    iframe.allowFullscreen = true;
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute(
      "allow",
      "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
    );
    iframe.src = source;
    iframe.dataset.gcLoaded = "true";
  };
  const unloadIframe = (iframe) => {
    if (!iframe || iframe.dataset.gcLoaded !== "true") return;
    iframe.src = "about:blank";
    iframe.dataset.gcLoaded = "false";
    iframe.dataset.gcStarted = "false";
  };
  const updatePlayButton = (card) => {
    if (!card) return;
    const button = card.querySelector(PLAY_SELECTOR);
    if (!button) return;
    const media = getActiveMedia(card);
    if (!media) {
      button.hidden = true;
      card.classList.remove("is-video-playing", "is-iframe-playing");
      return;
    }
    button.hidden = false;
    if (media.matches(VIDEO_SELECTOR)) {
      const playing = !media.paused && !media.ended;
      card.classList.toggle("is-video-playing", playing);
      card.classList.remove("is-iframe-playing");
      button.setAttribute(
        "aria-label",
        playing ? "Pausar video" : "Reproducir video"
      );
      return;
    }
    const started =
      media.dataset.gcStarted === "true" ||
      (
        media.dataset.gcLoaded === "true" &&
        isIframeAutoplay(media)
      );
    card.classList.remove("is-video-playing");
    card.classList.toggle("is-iframe-playing", started);
    button.setAttribute("aria-label", "Reproducir video");
  };
  const syncMediaCard = (card, visible) => {
    if (!card) return;
    const activeWrapper = getActiveWrapper(card);
    card.querySelectorAll("[data-gc-media-wrapper]").forEach((wrapper) => {
      const active = wrapper === activeWrapper;
      wrapper.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
        prepareVideo(video);
        if (active && visible && video.dataset.autoplay === "true") {
          playVideo(video, false).then(() => updatePlayButton(card));
        } else if (!active || !visible) {
          pauseVideo(video);
        }
      });
      wrapper.querySelectorAll(IFRAME_SELECTOR).forEach((iframe) => {
        if (active && visible && isIframeAutoplay(iframe)) {
          loadIframe(iframe, false);
        } else if (!active || !visible) {
          unloadIframe(iframe);
        }
      });
    });
    updatePlayButton(card);
  };
  const removeLegacyModal = () => {
    document.querySelectorAll(".gc-video-modal").forEach((modal) => {
      modal.remove();
    });
    document.documentElement.classList.remove("gc-video-modal-open");
  };
  const getFullscreenElement = () => {
    return document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement ||
      null;
  };
  const requestElementFullscreen = (element) => {
    if (!element) {
      return Promise.reject(
        new Error("Elemento de pantalla completa no disponible")
      );
    }
    if (typeof element.requestFullscreen === "function") {
      return element.requestFullscreen();
    }
    if (typeof element.webkitRequestFullscreen === "function") {
      element.webkitRequestFullscreen();
      return Promise.resolve();
    }
    if (typeof element.msRequestFullscreen === "function") {
      element.msRequestFullscreen();
      return Promise.resolve();
    }
    return Promise.reject(
      new Error("Pantalla completa no compatible")
    );
  };
  const storeInlineStyles = (element, properties) => {
    const values = {};
    properties.forEach((property) => {
      values[property] = element.style[property] || "";
    });
    return values;
  };
  const restoreInlineStyles = (element, values) => {
    if (!element || !values) return;
    Object.entries(values).forEach(([property, value]) => {
      element.style[property] = value;
    });
  };
  const cleanNativeFullscreenState = (root) => {
    const state = root._gcNativeFullscreenState;
    if (!state) return;
    const {
      media,
      card,
      wrapper,
      wrapperStyles,
      mediaStyles,
      wasPaused,
      wasMuted,
      hadControls,
      iframeWasLoaded
    } = state;
    if (media?.matches(VIDEO_SELECTOR)) {
      media.controls = hadControls;
      media.muted = wasMuted;
      media.defaultMuted = wasMuted;
      if (wasMuted) {
        media.setAttribute("muted", "");
      } else {
        media.removeAttribute("muted");
      }
      if (wasPaused) {
        pauseVideo(media);
      }
      updatePlayButton(card);
    } else if (media?.matches(IFRAME_SELECTOR)) {
      if (!iframeWasLoaded) {
        unloadIframe(media);
      }
      card?.classList.remove("is-iframe-playing");
      updatePlayButton(card);
    }
    restoreInlineStyles(wrapper, wrapperStyles);
    restoreInlineStyles(media, mediaStyles);
    wrapper?.classList.remove("gc-native-fullscreen-host");
    root.dataset.gcInteracting = "false";
    root._gcNativeFullscreenState = null;
  };
  const openNativeFullscreen = (root, card, trigger) => {
    const media = getActiveMedia(card);
    const wrapper = getActiveWrapper(card);
    if (!media || !wrapper) return;
    root.dataset.gcInteracting = "true";
    const state = {
      media,
      card,
      wrapper,
      trigger,
      wasPaused: media.matches(VIDEO_SELECTOR) ? media.paused : true,
      wasMuted: media.matches(VIDEO_SELECTOR) ? media.muted : true,
      hadControls: media.matches(VIDEO_SELECTOR) ? media.controls : false,
      iframeWasLoaded: media.matches(IFRAME_SELECTOR)
        ? media.dataset.gcLoaded === "true"
        : false,
      wrapperStyles: storeInlineStyles(wrapper, [
        "width",
        "height",
        "maxWidth",
        "maxHeight",
        "display",
        "placeItems",
        "background",
        "position",
        "inset"
      ]),
      mediaStyles: storeInlineStyles(media, [
        "width",
        "height",
        "maxWidth",
        "maxHeight",
        "objectFit",
        "objectPosition",
        "position",
        "inset",
        "background",
        "transform"
      ])
    };
    root._gcNativeFullscreenState = state;
    if (media.matches(VIDEO_SELECTOR)) {
      prepareVideo(media);
      media.style.width = "100vw";
      media.style.height = "100vh";
      media.style.maxWidth = "100vw";
      media.style.maxHeight = "100vh";
      media.style.objectFit = "contain";
      media.style.objectPosition = "center";
      media.style.background = "#000";
      media.style.transform = "none";
      media.controls = true;
      media.muted = false;
      media.defaultMuted = false;
      media.removeAttribute("muted");
      const enterNativeFullscreen = () => {
        if (
          typeof media.webkitEnterFullscreen === "function" &&
          !document.fullscreenEnabled &&
          !media.requestFullscreen
        ) {
          try {
            media.webkitEnterFullscreen();
            playVideo(media, true);
            return Promise.resolve();
          } catch (error) {
            return Promise.reject(error);
          }
        }
        return requestElementFullscreen(media).then(() => {
          return playVideo(media, true);
        });
      };
      enterNativeFullscreen().catch(() => {
        cleanNativeFullscreenState(root);
      });
      return;
    }
    loadIframe(media, true);
    wrapper.classList.add("gc-native-fullscreen-host");
    wrapper.style.width = "100vw";
    wrapper.style.height = "100vh";
    wrapper.style.maxWidth = "100vw";
    wrapper.style.maxHeight = "100vh";
    wrapper.style.display = "grid";
    wrapper.style.placeItems = "center";
    wrapper.style.background = "#000";
    wrapper.style.position = "fixed";
    wrapper.style.inset = "0";
    media.style.width = "100%";
    media.style.height = "100%";
    media.style.maxWidth = "100%";
    media.style.maxHeight = "100%";
    media.style.objectFit = "contain";
    media.style.objectPosition = "center";
    media.style.position = "absolute";
    media.style.inset = "0";
    requestElementFullscreen(media)
      .catch(() => requestElementFullscreen(wrapper))
      .catch(() => {
        cleanNativeFullscreenState(root);
      });
  };
  const initNativeFullscreen = (root) => {
    ensureVideoControls(root);
    const onExpandClick = (event) => {
      const button = event.target.closest(EXPAND_SELECTOR);
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      const card = button.closest(CARD_SELECTOR);
      if (card) {
        openNativeFullscreen(root, card, button);
      }
    };
    const onFullscreenChange = () => {
      if (!getFullscreenElement()) {
        cleanNativeFullscreenState(root);
      }
    };
    const onWebkitBeginFullscreen = (event) => {
      if (!root.contains(event.target)) return;
      root.dataset.gcInteracting = "true";
    };
    const onWebkitEndFullscreen = (event) => {
      if (!root.contains(event.target)) return;
      cleanNativeFullscreenState(root);
    };
    root.addEventListener("click", onExpandClick);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    document.addEventListener("MSFullscreenChange", onFullscreenChange);
    root.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      video.addEventListener(
        "webkitbeginfullscreen",
        onWebkitBeginFullscreen
      );
      video.addEventListener(
        "webkitendfullscreen",
        onWebkitEndFullscreen
      );
    });
    addCleanup(root, () => {
      root.removeEventListener("click", onExpandClick);
      document.removeEventListener(
        "fullscreenchange",
        onFullscreenChange
      );
      document.removeEventListener(
        "webkitfullscreenchange",
        onFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        onFullscreenChange
      );
      root.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
        video.removeEventListener(
          "webkitbeginfullscreen",
          onWebkitBeginFullscreen
        );
        video.removeEventListener(
          "webkitendfullscreen",
          onWebkitEndFullscreen
        );
      });
      cleanNativeFullscreenState(root);
    });
  };
  const initPlayControls = (root) => {
    ensureVideoControls(root);
    const onClick = (event) => {
      const button = event.target.closest(PLAY_SELECTOR);
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      const card = button.closest(CARD_SELECTOR);
      const media = card ? getActiveMedia(card) : null;
      if (!card || !media) return;
      if (media.matches(VIDEO_SELECTOR)) {
        if (!media.paused && !media.ended) {
          pauseVideo(media);
          updatePlayButton(card);
          return;
        }
        button.classList.add("is-loading");
        playVideo(media, true).then((played) => {
          button.classList.remove("is-loading");
          card.classList.toggle("is-video-playing", played);
          updatePlayButton(card);
        });
        return;
      }
      button.classList.add("is-loading");
      loadIframe(media, true);
      const finish = () => {
        button.classList.remove("is-loading");
        media.dataset.gcStarted = "true";
        card.classList.add("is-iframe-playing");
        updatePlayButton(card);
      };
      media.addEventListener("load", finish, { once: true });
      window.setTimeout(finish, 1300);
    };
    root.addEventListener("click", onClick);
    root.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      const card = video.closest(CARD_SELECTOR);
      const update = () => updatePlayButton(card);
      video.addEventListener("play", update);
      video.addEventListener("pause", update);
      video.addEventListener("ended", update);
      addCleanup(root, () => {
        video.removeEventListener("play", update);
        video.removeEventListener("pause", update);
        video.removeEventListener("ended", update);
      });
    });
    addCleanup(root, () => {
      root.removeEventListener("click", onClick);
    });
  };
  const initMedia = (root) => {
    const cards = Array.from(root.querySelectorAll(CARD_SELECTOR));
    if (!cards.length) return;
    cards.forEach((card) => {
      card.querySelectorAll(VIDEO_SELECTOR).forEach(prepareVideo);
      updatePlayButton(card);
    });
    if (!("IntersectionObserver" in window)) {
      cards.forEach((card) => {
        card.dataset.gcVisible = "true";
        syncMediaCard(card, true);
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          const visible = entry.isIntersecting;
          card.dataset.gcVisible = visible ? "true" : "false";
          syncMediaCard(card, visible);
        });
      },
      {
        threshold: 0.12,
        rootMargin: "120px 0px 120px 0px"
      }
    );
    cards.forEach((card) => observer.observe(card));
    const onMediaChange = () => {
      cards.forEach((card) => {
        card.querySelectorAll(VIDEO_SELECTOR).forEach(pauseVideo);
        card.querySelectorAll(IFRAME_SELECTOR).forEach(unloadIframe);
        syncMediaCard(
          card,
          card.dataset.gcVisible === "true"
        );
      });
    };
    if (typeof mobileQuery.addEventListener === "function") {
      mobileQuery.addEventListener("change", onMediaChange);
      addCleanup(root, () => {
        mobileQuery.removeEventListener("change", onMediaChange);
      });
    } else if (typeof mobileQuery.addListener === "function") {
      mobileQuery.addListener(onMediaChange);
      addCleanup(root, () => {
        mobileQuery.removeListener(onMediaChange);
      });
    }
    addCleanup(root, () => observer.disconnect());
  };
  const initReveal = (root) => {
    const elements = Array.from(
      root.querySelectorAll(REVEAL_SELECTOR)
    );
    if (!elements.length) return;
    const enabled = root.dataset.enableMotion === "true";
    const reducedMotion = window
      .matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    const stagger = Math.max(
      0,
      Number(root.dataset.revealStagger || 70)
    );
    if (
      !enabled ||
      reducedMotion ||
      !("IntersectionObserver" in window)
    ) {
      elements.forEach((element) => {
        element.classList.add("is-revealed");
      });
      return;
    }
    root.classList.add("gc-motion-ready");
    elements.forEach((element, index) => {
      element.style.transitionDelay =
        `${Math.min(index * stagger, 700)}ms`;
    });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -5% 0px"
      }
    );
    elements.forEach((element) => observer.observe(element));
    addCleanup(root, () => observer.disconnect());
  };
  const updateCarouselControls = (root) => {
    const track = getTrack(root);
    const controls = root.querySelector("[data-gc-controls]");
    const dots = root.querySelector("[data-gc-dots]");
    const prev = root.querySelector("[data-gc-prev]");
    const next = root.querySelector("[data-gc-next]");
    if (!track || !controls || !dots) return;
    const starts = getPageStarts(track);
    const activePage = getActivePage(track, starts);
    const hasOverflow =
      starts.length > 1 &&
      track.scrollWidth > track.clientWidth + 2;
    const loop = root.dataset.carouselLoop === "true";
    controls.hidden = !hasOverflow;
    track.classList.toggle("is-centered", !hasOverflow);
    root.classList.toggle(
      "has-carousel-overflow",
      hasOverflow
    );
    dots.innerHTML = "";
    if (hasOverflow) {
      starts.forEach((start, index) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className =
          `gc-carousel-dot${index === activePage ? " is-active" : ""}`;
        dot.setAttribute(
          "aria-label",
          `Ir al grupo ${index + 1}`
        );
        dot.setAttribute(
          "aria-current",
          index === activePage ? "true" : "false"
        );
        dot.addEventListener("click", () => {
          const duration = Math.max(
            200,
            Number(root.dataset.scrollSpeed || 600)
          );
          animateScroll(
            track,
            start,
            duration,
            () => updateCarouselControls(root)
          );
        });
        dots.appendChild(dot);
      });
    }
    if (prev) {
      prev.disabled =
        !hasOverflow ||
        (!loop && activePage <= 0);
    }
    if (next) {
      next.disabled =
        !hasOverflow ||
        (!loop && activePage >= starts.length - 1);
    }
  };
  const loopJump = (root, target) => {
    const track = getTrack(root);
    if (!track) return;
    cancelScrollAnimation(track);
    track.classList.add("is-loop-jumping");
    window.setTimeout(() => {
      track.scrollLeft = target;
      window.requestAnimationFrame(() => {
        track.classList.remove("is-loop-jumping");
        updateCarouselControls(root);
      });
    }, 150);
  };
  const moveCarousel = (root, direction) => {
    const track = getTrack(root);
    if (!track) return;
    const starts = getPageStarts(track);
    if (starts.length <= 1) return;
    const current = getActivePage(track, starts);
    const loop = root.dataset.carouselLoop === "true";
    const duration = Math.max(
      200,
      Number(root.dataset.scrollSpeed || 600)
    );
    let targetIndex = current + direction;
    if (targetIndex > starts.length - 1) {
      if (!loop) {
        targetIndex = starts.length - 1;
      } else {
        loopJump(root, starts[0]);
        return;
      }
    }
    if (targetIndex < 0) {
      if (!loop) {
        targetIndex = 0;
      } else {
        loopJump(root, starts[starts.length - 1]);
        return;
      }
    }
    animateScroll(
      track,
      starts[targetIndex],
      duration,
      () => updateCarouselControls(root)
    );
  };
  const initCarouselControls = (root) => {
    const track = getTrack(root);
    const prev = root.querySelector("[data-gc-prev]");
    const next = root.querySelector("[data-gc-next]");
    if (!track) return;
    let scrollFrame = null;
    let resizeTimer = null;
    const onScroll = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        updateCarouselControls(root);
        scrollFrame = null;
      });
    };
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        cancelScrollAnimation(track);
        updateCarouselControls(root);
      }, 120);
    };
    const onPrev = (event) => {
      event.preventDefault();
      event.stopPropagation();
      root.dataset.gcInteracting = "true";
      moveCarousel(root, -1);
      window.setTimeout(() => {
        root.dataset.gcInteracting = "false";
      }, 900);
    };
    const onNext = (event) => {
      event.preventDefault();
      event.stopPropagation();
      root.dataset.gcInteracting = "true";
      moveCarousel(root, 1);
      window.setTimeout(() => {
        root.dataset.gcInteracting = "false";
      }, 900);
    };
    track.addEventListener("scroll", onScroll, {
      passive: true
    });
    window.addEventListener("resize", onResize, {
      passive: true
    });
    prev?.addEventListener("click", onPrev);
    next?.addEventListener("click", onNext);
    window.requestAnimationFrame(() => {
      updateCarouselControls(root);
    });
    addCleanup(root, () => {
      window.clearTimeout(resizeTimer);
      if (scrollFrame) {
        window.cancelAnimationFrame(scrollFrame);
      }
      cancelScrollAnimation(track);
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      prev?.removeEventListener("click", onPrev);
      next?.removeEventListener("click", onNext);
    });
  };
  const initDrag = (root) => {
    const track = getTrack(root);
    if (!track) return;
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    const onPointerDown = (event) => {
      if (
        event.pointerType === "mouse" &&
        event.button !== 0
      ) return;
      if (
        event.target.closest(
          "a,button,iframe,video[controls]"
        )
      ) return;
      cancelScrollAnimation(track);
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      track.classList.add("is-dragging");
      track.setPointerCapture?.(event.pointerId);
      root.dataset.gcInteracting = "true";
    };
    const onPointerMove = (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 4) moved = true;
      track.scrollLeft = startScroll - delta;
    };
    const stopDragging = (event) => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove("is-dragging");
      track.releasePointerCapture?.(event.pointerId);
      const starts = getPageStarts(track);
      const active = getActivePage(track, starts);
      const duration = Math.max(
        200,
        Number(root.dataset.scrollSpeed || 600)
      );
      animateScroll(
        track,
        starts[active] || 0,
        Math.min(duration, 450),
        () => updateCarouselControls(root)
      );
      window.setTimeout(() => {
        root.dataset.gcInteracting = "false";
      }, 800);
    };
    const preventDraggedClick = (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    };
    track.addEventListener(
      "pointerdown",
      onPointerDown
    );
    track.addEventListener(
      "pointermove",
      onPointerMove
    );
    track.addEventListener(
      "pointerup",
      stopDragging
    );
    track.addEventListener(
      "pointercancel",
      stopDragging
    );
    track.addEventListener(
      "click",
      preventDraggedClick,
      true
    );
    addCleanup(root, () => {
      track.removeEventListener(
        "pointerdown",
        onPointerDown
      );
      track.removeEventListener(
        "pointermove",
        onPointerMove
      );
      track.removeEventListener(
        "pointerup",
        stopDragging
      );
      track.removeEventListener(
        "pointercancel",
        stopDragging
      );
      track.removeEventListener(
        "click",
        preventDraggedClick,
        true
      );
    });
  };
  const initCarouselAutoplay = (root) => {
    const track = getTrack(root);
    if (
      !track ||
      root.dataset.autoplay !== "true"
    ) return;
    const reducedMotion = window
      .matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    if (reducedMotion) return;
    const delay = Math.max(
      2000,
      Number(root.dataset.autoplayDelay || 5) * 1000
    );
    const pauseHover =
      root.dataset.pauseHover === "true";
    let timer = null;
    let rootVisible = true;
    let hovering = false;
    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };
    const canRun = () => {
      if (!rootVisible || document.hidden) return false;
      if (
        root.dataset.gcInteracting === "true"
      ) return false;
      if (pauseHover && hovering) return false;
      return true;
    };
    const advance = () => {
      if (!canRun()) return;
      const starts = getPageStarts(track);
      if (starts.length <= 1) return;
      moveCarousel(root, 1);
    };
    const start = () => {
      stop();
      timer = window.setInterval(advance, delay);
    };
    const onMouseEnter = () => {
      hovering = true;
    };
    const onMouseLeave = () => {
      hovering = false;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };
    if (pauseHover) {
      root.addEventListener(
        "mouseenter",
        onMouseEnter
      );
      root.addEventListener(
        "mouseleave",
        onMouseLeave
      );
      root.addEventListener(
        "focusin",
        onMouseEnter
      );
      root.addEventListener(
        "focusout",
        onMouseLeave
      );
    }
    let observer = null;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            rootVisible = entry.isIntersecting;
            if (rootVisible) {
              start();
            } else {
              stop();
            }
          });
        },
        { threshold: 0.08 }
      );
      observer.observe(root);
    }
    document.addEventListener(
      "visibilitychange",
      onVisibility
    );
    start();
    addCleanup(root, () => {
      stop();
      observer?.disconnect();
      document.removeEventListener(
        "visibilitychange",
        onVisibility
      );
      root.removeEventListener(
        "mouseenter",
        onMouseEnter
      );
      root.removeEventListener(
        "mouseleave",
        onMouseLeave
      );
      root.removeEventListener(
        "focusin",
        onMouseEnter
      );
      root.removeEventListener(
        "focusout",
        onMouseLeave
      );
    });
  };
  const parseCounterValue = (value) => {
    const normalized = String(value || "")
      .trim()
      .replace(/\s/g, "")
      .replace(/,/g, ".");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const formatCounterValue = (value, decimals) => {
    const locale =
      document.documentElement.lang || "es-CL";
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };
  const animateCounter = (
    counter,
    duration,
    reducedMotion
  ) => {
    if (
      !counter ||
      counter.dataset.gcAnimated === "true"
    ) return;
    counter.dataset.gcAnimated = "true";
    const target = parseCounterValue(
      counter.dataset.target
    );
    const decimals = clamp(
      Number(counter.dataset.decimals || 0),
      0,
      2
    );
    const output = counter.querySelector(
      "[data-gc-counter-value]"
    );
    if (!output) return;
    const complete = () => {
      output.textContent = formatCounterValue(
        target,
        decimals
      );
      counter.classList.add("is-counted");
    };
    if (reducedMotion || target === 0) {
      complete();
      return;
    }
    const startTime = performance.now();
    output.textContent = formatCounterValue(
      0,
      decimals
    );
    const easeOut = (progress) =>
      1 - Math.pow(1 - progress, 3);
    const frame = (time) => {
      const progress = Math.min(
        (time - startTime) / duration,
        1
      );
      output.textContent = formatCounterValue(
        target * easeOut(progress),
        decimals
      );
      if (progress < 1) {
        window.requestAnimationFrame(frame);
      } else {
        complete();
      }
    };
    window.requestAnimationFrame(frame);
  };
  const initCounters = (root) => {
    const counters = Array.from(
      root.querySelectorAll(COUNTER_SELECTOR)
    );
    if (!counters.length) return;
    const duration = Math.max(
      400,
      Number(root.dataset.counterDuration || 1400)
    );
    const reducedMotion = window
      .matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    if (
      reducedMotion ||
      !("IntersectionObserver" in window)
    ) {
      counters.forEach((counter) => {
        animateCounter(counter, duration, true);
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateCounter(
            entry.target,
            duration,
            false
          );
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.35,
        rootMargin: "0px 0px -5% 0px"
      }
    );
    counters.forEach((counter) => {
      observer.observe(counter);
    });
    addCleanup(root, () => observer.disconnect());
  };
  const initRoot = (root) => {
    if (
      !root ||
      root.hasAttribute(INIT_ATTR)
    ) return;
    root.setAttribute(INIT_ATTR, "true");
    root._gcCleanups = [];
    root.dataset.gcInteracting = "false";
    removeLegacyModal();
    initReveal(root);
    initPlayControls(root);
    initNativeFullscreen(root);
    initMedia(root);
    initCarouselControls(root);
    initDrag(root);
    initCarouselAutoplay(root);
    initCounters(root);
  };
  const destroyRoot = (root) => {
    if (!root) return;
    if (Array.isArray(root._gcCleanups)) {
      root._gcCleanups.forEach((cleanup) => {
        if (typeof cleanup === "function") {
          cleanup();
        }
      });
    }
    root._gcCleanups = [];
    root.removeAttribute(INIT_ATTR);
  };
  const initAll = (root = document) => {
    getRoots(root).forEach(initRoot);
  };
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initAll()
    );
  } else {
    initAll();
  }
  document.addEventListener(
    "shopify:section:load",
    (event) => initAll(event.target)
  );
  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      getRoots(event.target).forEach(destroyRoot);
    }
  );
  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const root =
        event.target.closest?.(ROOT_SELECTOR);
      if (!root) return;
      const block =
        event.target.closest?.("[data-gc-block]");
      if (!block) return;
      block.classList.add("is-revealed");
      block.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center"
      });
      if (block.matches(CARD_SELECTOR)) {
        block.dataset.gcVisible = "true";
        syncMediaCard(block, true);
        updatePlayButton(block);
      }
      window.setTimeout(() => {
        updateCarouselControls(root);
      }, 350);
    }
  );
})();